import { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";

/* ─────────────────────────────────────────────────────────────────────
   <GlobalReconnectBanner />
   Sticky banner that auto-shows on every page when Shopify OAuth is
   broken (token expired, scopes missing, last sync returned 401).
   Disappears the moment the next status check returns healthy data.
   Polls every 60s while mounted.
   ───────────────────────────────────────────────────────────────────── */

const POLL_MS = 60000;

export default function GlobalReconnectBanner() {
  const [status, setStatus] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const check = async () => {
    try {
      const s = await api.get("/shopify/status");
      setStatus(s.data || null);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_MS);
    return () => clearInterval(id);
  }, []);

  const tryResync = async () => {
    setRetrying(true);
    try {
      const r = await api.post("/shopify/sync", {
        sync_products: true, sync_orders: true, sync_customers: true,
        days_of_orders: 30,
      }, { timeout: 60000 });
      const errors = r.data?.synced?.errors || [];
      const has401 = errors.some(e => String(e).includes("401"));
      if (has401) {
        toast.error("Shopify still rejecting the token. Tap Reconnect to re-authorize.");
      } else {
        toast.success("Sync ran. Refreshing…");
        setTimeout(() => window.location.reload(), 1200);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-sync failed");
    }
    setRetrying(false);
  };

  if (!status?.connected || !status?.reauth_required) return null;

  const shop = status.shop;
  const tokenDead = status.health?.token_valid === false;
  const lastGood = status.health?.last_successful_sync;
  const reason = tokenDead
    ? "Shopify rejected the last sync (token invalid or revoked)."
    : `Write permissions missing: ${(status.missing_scopes || []).join(", ")}.`;

  const reconnect = () => {
    const target = status.reauth_url
      ? `${process.env.REACT_APP_BACKEND_URL}${status.reauth_url}`
      : "/integrations-settings";
    // OAuth consent can't render inside an iframe — always break out to top
    window.open(target, "_top");
  };

  return (
    <div
      className="sticky top-0 z-40 w-full bg-red-50 border-b border-red-200 px-4 py-2.5"
      data-testid="global-reconnect-banner"
      role="alert"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-red-100 border border-red-200 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-3.5 h-3.5 text-red-700" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-red-900 leading-tight">
              AI Growth Operator can't sync {shop || "your store"} — your numbers are stale
            </p>
            <p className="text-[11px] text-red-800/85 leading-tight mt-0.5">
              {reason} Reconnect now (takes 30 seconds) so projections, audits, and recommendations stay accurate.
              {lastGood && <span className="ml-1 opacity-80">Last good sync: {new Date(lastGood).toLocaleString()}.</span>}
              {status.client_id_masked && (
                <span className="ml-1 opacity-70" data-testid="reconnect-banner-appkey">
                  App key in use: {status.client_id_masked} — must match your Partner Dashboard Client ID.
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={tryResync}
            disabled={retrying}
            className="h-8 text-xs border-red-200 text-red-800 hover:bg-red-100"
            data-testid="reconnect-banner-retry"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${retrying ? "animate-spin" : ""}`} />
            {retrying ? "Retrying…" : "Retry sync"}
          </Button>
          <Button
            size="sm"
            onClick={reconnect}
            className="h-8 text-xs bg-red-200 hover:bg-red-300 text-red-900 border border-red-300 font-bold"
            data-testid="reconnect-banner-cta"
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            Reconnect Shopify
          </Button>
        </div>
      </div>
    </div>
  );
}
